import React from 'react';
import { FmHeading } from '../FmComposition';
import { useHostedHeading } from '../fmHostedHeading';
import { useElementContext } from './elementContext';
import {
  FmActionsElementView,
  FmLensElementView,
  FmNoticeElementView,
  FmProvenanceElementView,
} from './FmActionElements';
import { FmDocumentElementView } from './FmDocumentElement';
import { FmRankedListElementView, FmTimelineElementView } from './FmListElements';
import {
  FmChartElementView,
  FmImageElementView,
  FmMapElementView,
  type FmMediaCropRole,
} from './FmMediaElements';
import { FmTableElementView } from './FmTableElements';
import {
  FmCopyElementView,
  FmDefinitionListElementView,
  FmFactsElementView,
  FmKeyFiguresElementView,
  FmStepsElementView,
} from './FmTextElements';
import { keyedElements } from './elementKeys';
import type { FmElement, FmTableElement } from './elements.types';
import './FmElements.css';

/** A table names itself one level below the section copy; a title the host printed is dropped. */
const TitledTable: React.FC<{ element: FmTableElement }> = ({ element }) => {
  const { headingLevel } = useElementContext();
  const heading = useHostedHeading(element.title, (headingLevel + 1) as 3 | 4 | 5);
  if (!element.title || !heading.render) return <FmTableElementView element={element} />;
  return (
    <div className="fm-element-titled">
      <FmHeading level={Math.min(5, heading.level) as 3 | 4 | 5}>{element.title}</FmHeading>
      <FmTableElementView element={element} />
    </div>
  );
};

type FmReadingElement = Exclude<
  FmElement,
  { element: 'chart' | 'map' | 'image' | 'document' | 'table' | 'notice' | 'actions' | 'provenance' | 'lens' }
>;

/** The reading elements: words, numbers and ordered lists. */
function readingElementView(element: FmReadingElement): React.ReactElement {
  switch (element.element) {
    case 'copy':
      return <FmCopyElementView element={element} />;
    case 'keyFigures':
      return <FmKeyFiguresElementView element={element} />;
    case 'facts':
      return <FmFactsElementView element={element} />;
    case 'definitionList':
      return <FmDefinitionListElementView element={element} />;
    case 'rankedList':
      return <FmRankedListElementView element={element} />;
    case 'steps':
      return <FmStepsElementView element={element} />;
    case 'timeline':
      return <FmTimelineElementView element={element} />;
  }
}

/** Renders one typed element. Every element type has exactly one renderer. */
export const FmElementView: React.FC<{ element: FmElement; cropRole?: FmMediaCropRole }> = ({
  element,
  cropRole,
}) => {
  switch (element.element) {
    case 'chart':
      return <FmChartElementView element={element} />;
    case 'map':
      return <FmMapElementView element={element} cropRole={cropRole} />;
    case 'image':
      return <FmImageElementView element={element} cropRole={cropRole} />;
    case 'document':
      return <FmDocumentElementView element={element} />;
    case 'table':
      return <TitledTable element={element} />;
    case 'notice':
      return <FmNoticeElementView element={element} />;
    case 'actions':
      return <FmActionsElementView element={element} />;
    case 'provenance':
      return <FmProvenanceElementView element={element} />;
    case 'lens':
      return <FmLensElementView element={element} />;
    default:
      return readingElementView(element);
  }
};

/** Elements of one slot in descriptor order. */
export const FmElementList: React.FC<{
  elements: readonly FmElement[];
  cropRole?: FmMediaCropRole;
}> = ({ elements, cropRole }) => (
  <>
    {keyedElements(elements).map(({ key, element }) => (
      <FmElementView key={key} element={element} cropRole={cropRole} />
    ))}
  </>
);
